import { baseApi, getErrorMessage } from '@/shared/api';
import { CreativeAssetType, CreativeItemType } from '../model/types';
import type { CreativeItem } from '../model/types';
import type {
  CreativesAuthDTO,
  CreativeItemDTO,
  CreativesSearchDTO,
} from './types';

const toCreativeItemType = (raw: string): CreativeItemType =>
  Object.values(CreativeItemType).includes(raw as CreativeItemType)
    ? (raw as CreativeItemType)
    : CreativeItemType.Unknown;

const toCreativeAssetType = (raw: string): CreativeAssetType =>
  Object.values(CreativeAssetType).includes(raw as CreativeAssetType)
    ? (raw as CreativeAssetType)
    : CreativeAssetType.Image;

const mapCreative = (dto: CreativeItemDTO): CreativeItem => ({
  creativeId: dto.creativeId,
  creativeName: dto.creativeName,
  adId: dto.adId,
  adName: dto.adName,
  accountId: dto.accountId,
  sourceName: dto.sourceName,
  type: toCreativeItemType(dto.type),
  thumbnailUrl: dto.thumbnailUrl,
  videoPreviewUrl: dto.videoPreviewUrl,
  assets: dto.assets.map((asset) => ({
    id: asset.id,
    type: toCreativeAssetType(asset.type),
    url: asset.url,
    previewImageUrl: asset.preview_image_url,
  })),
});

const CLOUDFRONT_COOKIES = [
  'CloudFront-Policy',
  'CloudFront-Signature',
  'CloudFront-Key-Pair-Id',
] as const;

// Returns "; Domain=.example.com" for subdomains, or "" for localhost/single-segment hosts.
// CloudFront images are served from a subdomain of the same parent domain, so the cookies
// must be scoped to the parent domain to be included in cross-subdomain image requests.
const getCookieDomain = (): string => {
  const parts = globalThis.location.hostname.split('.');
  return parts.length >= 2 ? `; Domain=.${parts.slice(-2).join('.')}` : '';
};

export const clearCreativesCookies = (): void => {
  const domain = getCookieDomain();
  for (const name of CLOUDFRONT_COOKIES) {
    document.cookie = `${name}=; Path=/${domain}; Secure; SameSite=None; Max-Age=0`;
  }
};

export const authCreatives = async (): Promise<void> => {
  const { data, error } = await baseApi.functions.invoke<CreativesAuthDTO>(
    'creatives/auth',
    { method: 'GET' },
  );

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  const domain = getCookieDomain();
  document.cookie = `CloudFront-Policy=${data.policy}; Path=/${domain}; Secure; SameSite=None`;
  document.cookie = `CloudFront-Signature=${data.signature}; Path=/${domain}; Secure; SameSite=None`;
  document.cookie = `CloudFront-Key-Pair-Id=${data.key_pair_id}; Path=/${domain}; Secure; SameSite=None`;
};

export const searchCreatives = async (
  items: { ad_id: string; ad_name: string; media_source: string }[],
): Promise<CreativeItem[]> => {
  const { data, error } = await baseApi.functions.invoke<CreativesSearchDTO>(
    'creatives/search',
    {
      method: 'POST',
      body: {
        adIds: [...new Set(items.map((i) => i.ad_id))],
        adNames: [...new Set(items.map((i) => i.ad_name))],
        mediaSources: [...new Set(items.map((i) => i.media_source))],
      },
    },
  );

  if (!data) {
    throw new Error(getErrorMessage(error));
  }

  return data.items.map(mapCreative);
};

